import { INullableAddress } from '@commenda/types';
import { Country, OwnershipRelationshipType, StockClassType } from '@prisma/client';

export const BeneficialOwnerControlType = {
  DIRECT_OWNERSHIP: 'DIRECT_OWNERSHIP',
  INDIRECT_OWNERSHIP: 'INDIRECT_OWNERSHIP',
  DIFFERENTIAL_VOTING_RIGHTS: 'DIFFERENTIAL_VOTING_RIGHTS',
  APPOINT_SENIOR_MANAGEMENT: 'APPOINT_SENIOR_MANAGEMENT',
  DEBT_INSTRUMENTS: 'DEBT_INSTRUMENTS',
  POSITIONS_WITHIN_LEGAL_PERSON: 'POSITIONS_WITHIN_LEGAL_PERSON',
  ANOTHER_PERSON: 'ANOTHER_PERSON',
  OTHER: 'OTHER',
} as const;

export type BeneficialOwnerControlType = (typeof BeneficialOwnerControlType)[keyof typeof BeneficialOwnerControlType];

export const ShareholdingType = {
  INDIVIDUAL: 'INDIVIDUAL',
  INSTITUTION: 'INSTITUTION',
} as const;

export type ShareholdingType = (typeof ShareholdingType)[keyof typeof ShareholdingType];

export const ShareholdingTypeLabel = {
  INDIVIDUAL: 'Individual',
  INSTITUTION: 'Entity',
} as const satisfies Record<ShareholdingType, string>;

export type Shareholding = {
  stakeholdingId: string;
  name: string;
  type: ShareholdingType;
  // Per-class % of this shareholding's stock class.
  ownershipPercent: number | null;
  // Effective % of the corp's total authorized shares. Falls back to
  // `ownershipPercent` when any class on the corp lacks share counts (legacy).
  dilutedOwnershipPercent: number | null;
  nationality: Country | null;
  relationshipType: OwnershipRelationshipType;
  hasSignificantControl: boolean;
  // Derived from the matching BOR's directControlType; null when no BOR exists
  // or BOR has no directControlType.
  controlType: BeneficialOwnerControlType | null; // individual rows only
  directorId: number | null; // individual rows only
  incorporationCountry: Country | null; // institution rows only — use `nationality` for individuals
  stakeholderCorporationId: number | null;
  // null = unspecified; UI renders "—".
  stockClass: { id: string; name: string; type: StockClassType } | null;
};

export type OwnershipBeneficialOwner = {
  id: string;
  directorId: number;
  name: string;
  // Per-corp direct control type (when this BOR was added as a direct UBO).
  directControlType: BeneficialOwnerControlType | null;
  // Aggregated paths (each parent corp through which this person is a UBO).
  paths: BeneficialOwnerPath[];
};

export type BeneficialOwnerPath = {
  sourceCorporationId: number;
  // Null when the source corp is an external (non-controlled) entity whose
  // name we don't expose to this workspace.
  sourceCorporationName: string | null;
  controlType: BeneficialOwnerControlType;
};

export type OwnershipRecentDocument = {
  id: number;
  fileName: string;
  fileUrl: string;
  extension: string | null;
  uploadedAt: string; // ISO 8601
};

export type OwnershipPaginationDto = {
  offset: number;
  limit: number;
  total: number;
  hasMore: boolean;
};

export type OwnershipResponse = {
  entity: {
    id: number;
    name: string | null;
    incorporationCountry: Country;
    // Whole-percent UBO threshold for this corp's jurisdiction (not scaled).
    uboThresholdPercent: number;
    companyId: number;
  };
  lastUpdatedAt: string | null;
  percentScale: number;
  totalOwnershipPercent: number;
  shareholdings: Shareholding[];
  pagination: OwnershipPaginationDto;
  beneficialOwners: OwnershipBeneficialOwner[];
  recentDocuments: OwnershipRecentDocument[];
};

export type InheritableUltimateBeneficialOwner = {
  directorId: number;
  name: string;
  nationality: Country | null;
};

// Inline stock-class draft staged by the FE picker. Submitted alongside a
// shareholding write so the BE creates the StockClass in the same tx —
// cancelling the form leaves no orphan class in the corp's pool.
export type NewStockClassDraft = {
  name: string;
  type: StockClassType;
  authorizedShares: number;
};

// Identity + nationality for creating a brand-new director alongside the
// shareholding. Used only when `directorId` is absent in the parent
// payload — ownership flows never edit an existing person.
export type CreatePersonDraft = {
  firstName: string;
  lastName?: string;
  dateOfBirth: string; // YYYY-MM-DD
  // Seeds both Director.personalDetails.countryOfCitizenship and the
  // shareholding's Stakeholder.nationality on first-time creation.
  nationality: Country;
};

// Exactly one of `directorId` / `person` must be set. When `directorId`
// is set, the BE derives nationality from the director's existing
// `personalDetails.countryOfCitizenship`.
export type CreateIndividualShareholdingPayload = {
  directorId?: number;
  person?: CreatePersonDraft;
  ownedShares: number; // canonical owned-share count
  relationshipType: OwnershipRelationshipType;
  hasSignificantControl?: boolean;
  // Required when hasSignificantControl=true, forbidden otherwise.
  directControlType?: BeneficialOwnerControlType;
  stockClassId?: string;
  newStockClass?: NewStockClassDraft;
  // When true, after the resolved class is set on this row, the BE assigns
  // it to every other Shareholding on the same corp currently on the null
  // class — atomic with the Shareholding write.
  applyToUnassignedShareholdings?: boolean;
};

export type InheritedUboSelection = {
  directorId: number;
  controlType: BeneficialOwnerControlType;
};

export type CreateInstitutionShareholdingPayload = {
  stakeholderCorporationId: number;
  ownedShares: number; // canonical owned-share count
  relationshipType: OwnershipRelationshipType;
  hasSignificantControl?: boolean;
  // Exact desired set inherited from the linked entity. Omit to leave the
  // graph alone; [] drops all paths from this parent.
  inheritedUbos?: InheritedUboSelection[];
  stockClassId?: string;
  newStockClass?: NewStockClassDraft;
  applyToUnassignedShareholdings?: boolean;
};

// Discriminated envelope sent to POST /ownership/:corp/shareholdings.
export type CreateShareholdingRequest =
  | { type: 'INDIVIDUAL'; individual: CreateIndividualShareholdingPayload }
  | { type: 'INSTITUTION'; institution: CreateInstitutionShareholdingPayload };

export type UpdateIndividualShareholdingPayload = {
  ownedShares?: number; // canonical owned-share count
  hasSignificantControl?: boolean;
  // Required when turning HSC on; forbidden when turning HSC off.
  directControlType?: BeneficialOwnerControlType;
  relationshipType?: OwnershipRelationshipType;
  stockClassId?: string;
  newStockClass?: NewStockClassDraft;
  applyToUnassignedShareholdings?: boolean;
};

export type UpdateInstitutionShareholdingPayload = {
  ownedShares?: number; // canonical owned-share count
  hasSignificantControl?: boolean;
  // Same semantics as CreateInstitutionShareholdingPayload.inheritedUbos.
  // Must be [] or omitted when HSC is being turned off.
  inheritedUbos?: InheritedUboSelection[];
  relationshipType?: OwnershipRelationshipType;
  stockClassId?: string;
  newStockClass?: NewStockClassDraft;
  applyToUnassignedShareholdings?: boolean;
};

// Discriminated envelope sent to PATCH /ownership/:corp/shareholdings/:id.
export type UpdateShareholdingRequest =
  | { type: 'INDIVIDUAL'; individual: UpdateIndividualShareholdingPayload }
  | { type: 'INSTITUTION'; institution: UpdateInstitutionShareholdingPayload };

// Discriminated result of POST /shareholdings. Each branch returns the
// linked id so the caller can revalidate the corresponding domain
// queries (persons for INDIVIDUAL, business entities for INSTITUTION)
// without re-fetching the ownership snapshot.
export type CreateShareholdingResult =
  | { type: 'INDIVIDUAL'; stakeholdingId: string; directorId: number }
  | { type: 'INSTITUTION'; stakeholdingId: string; stakeholderCorporationId: number };

export type UpdateShareholdingResult = {
  stakeholdingId: string;
};

// Person draft used by the atomic BOR-create path. Same shape as the
// AddPersonBottomsheet stages locally before submit.
export type CreateBeneficialOwnerPersonDraft = {
  firstName: string;
  lastName?: string;
  dateOfBirth: string;
  nationalities: Country[];
};

// Caller passes EITHER directorId (existing person) OR person (atomic
// Director + BOR create on submit — no orphan Director if the form is
// abandoned).
export type CreateBeneficialOwnerRequest = {
  directorId?: number;
  person?: CreateBeneficialOwnerPersonDraft;
  kind: 'direct' | 'manual';
  directControlType?: BeneficialOwnerControlType;
};

export type CreateBeneficialOwnerResult = { borId: string };

export type DeleteAllBeneficialOwnersResult = {
  totalRemoved: number;
};

export type DirectorDeletePreview = {
  keyPersonOf: Array<{ corporationId: number; corporationName: string }>;
  uboOf: Array<{ corporationId: number; corporationName: string }>;
  cascadeRemoves: Array<{
    corporationId: number;
    corporationName: string;
    through: Array<{ corporationId: number; corporationName: string }>;
  }>;
};

export type UpdateBeneficialOwnerRequest = {
  effectiveControlType: BeneficialOwnerControlType;
};

// One row from the "Edit share classes" table. `authorizedShares` always
// resolves the class to AuthorizedSharesType.CUSTOM — this UI only ever
// edits a concrete total, never "Unlimited"/"Not Applicable".
export type UpdateShareClassTotalSharesRequest = {
  shareClassId: string;
  authorizedShares: number;
};
